import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import isUndefined from "lodash/isUndefined";
import isEmpty from "lodash/isEmpty";
import { AnimatePresence } from "motion/react";
import { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import Skeleton from "react-loading-skeleton";
import { Link, useLocation, useNavigate } from "react-router";
import { ICONS } from "../../assets/icons";
import { IMAGES } from "../../assets/images";
import DayTimesTab from "../../components/dayTimesTab";
import Header from "../../components/header";
import ProfileRow from "../../components/profileRow";
import SearchInput from "../../components/searchInput";
import { CONSTANTS, RoutesType } from "../../constants";
import PillModal from "../../modals/pillsModal";
import { fetcher } from "../../network";
import { API } from "../../network/api";
import {
  fetchDoctors,
  patientsQueryKey,
  useSaveHistoryMutation,
} from "../../network/globalFetches";
import { Patient, TimeToTakePills, ToastOptionType } from "../../types";
import {
  ArchivePatientResponse,
  PatientsResponse,
} from "../../types/responses";
import Drawer from "./components/drawer";

const fetchPatients = async () => {
  const { data } = await fetcher().get<PatientsResponse>(API.GET.PATIENTS);
  return data.patients;
};

const mutateArchivePatient = async (
  id: string
): Promise<ArchivePatientResponse> => {
  const { data } = await fetcher().post(API.POST.ARCHIVE_PATIENT(id));
  return data;
};

const renderContent = (
  filterPatients: Patient[],
  onItemClick: (patient: Patient) => void,
  archivePatient: (id: string) => void,
  editPatient: (patient: Patient) => void,
  onPillsClick: (patientIndex: number) => void,
  loadingMutation: boolean,
  isLoadingData: boolean,
  isError: boolean,
  timeFilter: TimeToTakePills | ""
) => {
  if (isError) {
    return (
      <div className="flex-1 flex flex-col w-4/5 mx-auto scrollbar-hide mt-10 items-center">
        <div className="flex flex-col text-center gap-8 h-1/2 w-1/2">
          <h1 className="text-2xl">
            אופס... השרת יצא להפסקת קפה. תן לו כמה דקות ותנסה שוב! ☕
          </h1>
          <img src={ICONS.ERROR_NOT_FOUND} />
        </div>
      </div>
    );
  }

  if (isLoadingData) {
    return (
      <div className="flex-1 overflow-y-auto flex flex-col w-4/5 mx-auto scrollbar-hide mt-10">
        <div className="text-center py-4">
          <Skeleton count={5} height={65} />
        </div>
      </div>
    );
  }

  if (isEmpty(filterPatients)) {
    return (
      <div className="flex flex-col text-center items-center gap-10">
        <h2 className="font-bold text-xl">לא נמצאו דיירים</h2>

        <div className="hover:scale-110 shadow">
          <Link
            to={CONSTANTS.ROUTES.addPatients}
            className="bg-[#144082] text-white rounded py-3 px-8">
            הוסף דייר +
          </Link>
        </div>

        <div className="w-1/2">
          <img
            className="w-full "
            src={IMAGES.NOT_FOUND_PATIENTS}
            alt="לא נמצאו דיירים"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col w-11/12 sm:w-4/5 mx-auto scrollbar-hide pb-4 min-h-[80vh]">
      <div className="text-center py-4">
        <AnimatePresence>
          {filterPatients.map((patient, index) => {
            return (
              <ProfileRow
                key={patient._id}
                item={patient}
                onPillsClick={() => onPillsClick(index)}
                archivePatient={archivePatient}
                onEditPatient={editPatient}
                onItemClick={onItemClick}
                loadingMutation={loadingMutation}
                isLastItem={index === filterPatients.length - 1}
                timeFilter={timeFilter}
              />
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
};

const Home = () => {
  const [filterPatients, setFilterPatients] = useState<Patient[]>([]);
  const [timeFilter, setTimeFilter] = useState<TimeToTakePills | "">("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isPillModalOpen, setIsPillModalOpen] = useState(false);
  const [
    currentPatientIndexForPillShortcut,
    setCurrentPatientIndexForPillShortcut,
  ] = useState<number>();

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const locationState = location.state as
    | (ToastOptionType & { from?: RoutesType })
    | undefined;

  const { data, isError, isLoading } = useQuery({
    queryKey: patientsQueryKey,
    queryFn: fetchPatients,
    staleTime: Infinity,
  });

  const { data: doctors } = useQuery({
    queryKey: fetchDoctors.queryKey,
    queryFn: fetchDoctors.fetch,
    staleTime: Infinity,
  });

  const { mutate: mutateArchive, isPending: loadingMutation } = useMutation<
    ArchivePatientResponse,
    Error,
    string
  >({
    mutationFn: mutateArchivePatient,
    onSuccess: ({ archivedPatient }) => {
      queryClient.setQueryData<Patient[]>(patientsQueryKey, (oldData) => {
        if (!oldData) return [];
        return oldData.filter((patient) => patient._id !== archivedPatient._id);
      });
      toast.success(`הדייר ${archivedPatient.name} עבר לארכיון`);
    },
    onError: () => {
      toast.error(
        "תקלת מערכת - הדייר לא מוכן לצאת מהבית. לצערנו לא הצלחנו להעבר רותו לארכיון 🤷🏻‍♂️"
      );
    },
  });

  useEffect(() => {
    if (isUndefined(data)) {
      return;
    }
    let filtered = data;

    if (!isEmpty(timeFilter)) {
      filtered = filtered.filter((patient) =>
        patient.timeToTakePills.includes(timeFilter)
      );
    }

    if (searchTerm) {
      filtered = filtered.filter((patient) =>
        patient.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilterPatients(filtered);
  }, [timeFilter, searchTerm, data]);

  useEffect(() => {
    if (locationState) {
      if (locationState.toastOption && locationState.from) {
        const { toastOption, from } = locationState;
        if (from === CONSTANTS.ROUTES.addPatients) {
          toast.success(toastOption.title);
        }
      }
    }
  }, [locationState]);

  const toggleDrawer = () => {
    setIsDrawerOpen((prev) => !prev);
  };

  const editPatient = (patient: Patient) => {
    navigate(CONSTANTS.ROUTES.addPatients, {
      state: { patient },
    });
  };

  const onPatientClick = (patient: Patient) => {
    navigate(CONSTANTS.ROUTES.patientDetails, {
      state: { patientId: patient._id },
    });
  };

  const onPillsClick = (patientIndex: number) => {
    setIsPillModalOpen(true);
    setCurrentPatientIndexForPillShortcut(patientIndex);
  };

  const onNext = () => {
    setCurrentPatientIndexForPillShortcut((prev) => {
      return prev === undefined || prev === filterPatients.length - 1
        ? 0
        : prev + 1;
    });
  };

  const onPrev = () => {
    setCurrentPatientIndexForPillShortcut((prev) => {
      return prev ? prev - 1 : filterPatients.length - 1;
    });
  };

  const { mutate, isPending } = useSaveHistoryMutation({
    onSuccess: () => {
      toast.success("נשמר להסטוריה בהצלחה");
      onNext();
    },
    onError: () => {
      toast.error("שגיאה - לא הצלחנו לשמור להסטוריה");
    },
  });

  return (
    <div className="container mx-auto h-screen flex flex-col">
      <div className="mx-2">
        <Header>
          <Header.Time />
          <Header.Logo />
          <Header.Menu toggleDrawer={toggleDrawer} />
        </Header>

        {!isError && (
          <main className="my-6">
            <div className="w-2/3 sm:w-1/3 mx-auto">
              <SearchInput placeholder="חפש דייר" onSearch={setSearchTerm} />
            </div>
            <div className="hidden sm:flex justify-center my-8">
              <div className="bg-slate-100 p-4 rounded border">
                <h2 className="text-center pb-4 font-bold">
                  סינון לפי זמני לקיחת כדורים
                </h2>
                <p className="text-center text-md pb-4">
                  לחץ להפעלת\כיבוי הסינון
                </p>
                <DayTimesTab onTabChange={setTimeFilter} />
              </div>
            </div>
          </main>
        )}
      </div>

      {!isEmpty(filterPatients) && !isError && (
        <p className="w-4/5 mx-auto">מספר דיירים: {filterPatients.length}</p>
      )}

      {renderContent(
        filterPatients,
        onPatientClick,
        mutateArchive,
        editPatient,
        onPillsClick,
        loadingMutation,
        isLoading,
        isError,
        timeFilter
      )}

      <div className="fixed bottom-6 right-6 flex flex-col gap-4">
        <Link
          to={CONSTANTS.ROUTES.addPatients}
          className="text-xs sm:text-sm bg-[#144082] text-white rounded py-2 px-4 sm:py-2 sm:px-2 hover:scale-110 shadow ">
          הוסף דייר +
        </Link>
      </div>
      {isDrawerOpen && <Drawer toggleDrawer={toggleDrawer} />}
      <Toaster position="bottom-center" />
      {isPillModalOpen && currentPatientIndexForPillShortcut !== undefined && (
        <PillModal
          isPending={isPending}
          isShortcut={true}
          doctors={doctors}
          onClose={() => setIsPillModalOpen(false)}
          patient={filterPatients[currentPatientIndexForPillShortcut]}
          onNext={onNext}
          onPrev={onPrev}
          onSave={({ data, guideId, patientId }) => {
            mutate({
              date: dayjs().toISOString(),
              guideId,
              patientId,
              pills: data,
            });
          }}
        />
      )}
    </div>
  );
};

export default Home;
